import { withCompInfo } from '@wix/editor-elements-integrations'
import type {
	Design,
	VectorImageCSS,
	VectorImageCssVars,
	VectorImageDefinition,
	VectorImageProps,
	VectorImagePropsWithSvgFeatureExperiment,
} from './VectorImage'
import type { ParsedSvg } from '@wix/thunderbolt-commons'
import { getCSSDropShadow, transformVectorImage, SVG_TYPES } from '@wix/thunderbolt-commons'
import type { CompDataVectorImage } from './VectorImageHelpers'
import { getSvgType, getVectorImageCSS, transformStrokeWidth } from './VectorImageHelpers'
import { mapKeys, pickBy } from 'lodash'
import type { AbsoluteLayout, ColorMap, VectorImageData } from '@wix/thunderbolt-becky-types'

export const css = withCompInfo<VectorImageCssVars | {}, VectorImageDefinition>()(
	['compDesign', 'compData', 'compProps', 'hasResponsiveLayout', 'siteColors', 'styleProperties'],
	({ compDesign, compData, compProps, hasResponsiveLayout, siteColors, styleProperties }) => {
		const shadow = compDesign?.shadow
		const useCSSDropShadow = shadow && hasResponsiveLayout
		const dropShadow = useCSSDropShadow && getCSSDropShadow(shadow, siteColors)

		const resolvedSvg = (compData as CompDataVectorImage).resolvedSvg
		const vectorImageCss: VectorImageCSS =
			getVectorImageCSS({
				resolvedSvg,
				design: compDesign,
				compProps,
				styleProperties,
				siteColors,
			}) || {}

		const vars = mapKeys(vectorImageCss, (__, key) => `--${key || ''}`)

		return {
			...pickBy(vars, (__, key) => key !== '--transform'),
			...(vars['--transform'] ? { '--flip': vars['--transform'] } : {}),
			...(useCSSDropShadow ? { '--drop-shadow': dropShadow } : {}),
		} as VectorImageCssVars
	}
)

const extractTransformationOptions = (
	compId: string,
	colorsMap: ColorMap,
	design: Design,
	compData: VectorImageData,
	compProps: any,
	compLayout: Partial<AbsoluteLayout>
) => {
	const layout = {
		width: compLayout.width,
		height: compLayout.height,
	}
	const { svgId, alt: altText } = compData as any
	const { shapeStyle = {}, overrideColors = {}, shadow = {} } = design as any
	const { strokeWidth } = transformStrokeWidth(shapeStyle)
	return {
		svgId,
		altText,
		compId,
		strokeWidth,
		properties: compProps,
		layout,
		shadow,
		colorsMap,
		overrideColors,
	}
}

export const props = withCompInfo<VectorImageProps | VectorImagePropsWithSvgFeatureExperiment, VectorImageDefinition>()(
	// @ts-ignore
	['experiments', 'compDesign', 'compData', 'compProps', 'compLayout', 'compId', 'colorsMap', 'hasResponsiveLayout'],
	// @ts-ignore
	({ experiments, compDesign, compData, compProps, compLayout, compId, colorsMap, hasResponsiveLayout }) => {
		const shouldUseSvgLoaderFeature = experiments['specs.thunderbolt.useSvgLoaderFeature']
		const design: any = compDesign || {}
		const transformationOptions = extractTransformationOptions(
			compId,
			colorsMap,
			design,
			compData,
			compProps,
			compLayout
		)
		const { svgId: svg, link = {} } = compData
		const { info: svgInfo, content: svgString } = (svg as unknown) as ParsedSvg
		const svgType = getSvgType(svgInfo)
		const options: any = {
			...transformationOptions,
			svgInfo,
			isResponsive: hasResponsiveLayout,
		}
		const svgContent = shouldUseSvgLoaderFeature ? '' : transformVectorImage(svgString, options)
		const shouldScaleStroke = svgType === SVG_TYPES.UGC
		const withShadow = !!design.shadow
		const withStroke = !!options.strokeWidth
		return {
			withShadow,
			link,
			...(shouldUseSvgLoaderFeature ? {} : { svgContent, withStroke, shouldScaleStroke }),
		}
	}
)
